import React, { Fragment, useState } from "react";
import CommonDialog from "../../common/components/CommonDialog";
import { Box, Collapse, IconButton, Typography, useTheme } from "@mui/material";
import CommonButton from "../../common/components/CommonButton";
import moment from "moment";
import { CalendarEventType, EventStatus, Reminders, Repeats, UserTypeId } from "../../common/const";
import CreateEventDialog from "./CreateEventDialog";
import { BorderColor, Check, Close, DeleteForever, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { deleteCalendarEvent, updateCalenderEvent } from "../../api/calendarApi";

const ViewEventDialog = ({
    viewOpen,
    setViewOpen,
    viewData,
    getEvents,
    getAllEvents,
    eventList
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { loading, user } = useSelector((state) => state.auth);
    const [eventOpen, setEventOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [eventId, setEventId] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [expanded, setExpanded] = useState(1);
    const events = eventList.filter((a) => {
        if (!a.event_start_timestamp || !viewData.event_start_timestamp) return false;

        // Convert UTC timestamps to local timezone and compare dates
        const aDate = moment.utc(a.event_start_timestamp).local().format('YYYY-MM-DD');
        const viewDataDate = moment.utc(viewData.event_start_timestamp).local().format('YYYY-MM-DD');

        return aDate === viewDataDate;
    });

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            {eventOpen && (
                <CreateEventDialog
                    eventOpen={eventOpen}
                    setEventOpen={setEventOpen}
                    getEvents={getEvents}
                    getAllEvents={getAllEvents}
                    selectedEvent={selectedEvent}
                    setSelectedEvent={setSelectedEvent}
                    setViewOpen={setViewOpen}
                    isEdit
                />
            )}
            <CommonDialog
                open={deleteOpen}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to remove {viewData.title} event?
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                try {
                                    dispatch(setLoading({
                                        loading: true
                                    }));
                                    const response = await deleteCalendarEvent(eventId);
                                    if (response.data.success) {
                                        showNotification('Calendar event deleted successfully', 'success');
                                        setDeleteOpen(false);
                                        setViewOpen(false);
                                        setEventId(null);
                                        dispatch(setLoading({
                                            loading: false
                                        }));
                                        getEvents();
                                        if (getAllEvents) {
                                            getAllEvents();
                                        }
                                    }
                                } catch (err) {
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setEventId(null);
                                setDeleteOpen(false);
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonDialog
                open={viewOpen}
                size="xs"
                title={
                    <Fragment>
                        <Box sx={{ float: 'right' }}>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setViewOpen(false);
                                }}
                            />
                        </Box>
                    </Fragment>
                }
                content={
                    <Box mt={1}>
                        {events.map((item, index) => {
                            const isExpanded = expanded === item.event_id;
                            return (
                                <Box
                                    key={item.event_id}
                                    sx={{
                                        pb: 1,
                                        pt: 1,
                                        borderBottom: index < events.length - 1 ? '1px solid #7A869126' : 'none',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            cursor: 'pointer',
                                            justifyContent: 'space-between'
                                        }}
                                        onClick={() => setExpanded(isExpanded ? null : item.event_id)}
                                    >
                                        <Box>
                                            <Box mb={1}>
                                                <Typography sx={{ fontWeight: 600, fontSize: 17 }}>{item?.event_title}</Typography>
                                                <Typography sx={{ fontSize: 13 }} color={theme.palette.background.dark}>{moment.utc(item?.event_start_timestamp).format('dddd, MMMM, DD, yyyy')}</Typography>
                                            </Box>
                                            <Box
                                                display="flex"
                                                gap={1}
                                            >
                                                <Box
                                                    sx={{
                                                        background: item.event_category === CalendarEventType.application ? '#f8dff1ff'
                                                            : item.event_category === CalendarEventType.exam ? '#ecf5f8ff'
                                                                : item.event_category === CalendarEventType.task ? '#e0efe6ff'
                                                                    : item.event_category === CalendarEventType.counselling ? '#faf1e4ff'
                                                                        : '#eceff5ff',
                                                        padding: '3px 15px',
                                                        marginBottom: 0.5,
                                                        borderRadius: 1,
                                                        border: `1px solid ${item.event_category === CalendarEventType.application ? '#E328AF'
                                                            : item.event_category === CalendarEventType.exam ? '#5DCFFF'
                                                                : item.event_category === CalendarEventType.task ? '#00C950'
                                                                    : item.event_category === CalendarEventType.counselling ? '#FFAB2D'
                                                                        : '#6A7282'}`
                                                    }}
                                                >
                                                    <Typography fontSize={13} color={
                                                        item.event_category === CalendarEventType.application ? '#E328AF'
                                                            : item.event_category === CalendarEventType.exam ? '#5DCFFF'
                                                                : item.event_category === CalendarEventType.task ? '#00C950'
                                                                    : item.event_category === CalendarEventType.counselling ? '#FFAB2D'
                                                                        : '#6A7282'}>{item.event_category}</Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        background: item.priority === 'MEDIUM' ? '#FEF9C2' : item.priority === 'HIGH' ? '#FFE2E2' : theme.palette.success.light,
                                                        padding: '3px 15px',
                                                        marginBottom: 0.5,
                                                        borderRadius: 1,
                                                        border: `1px solid ${item.priority === 'MEDIUM' ? '#894B00' : item.priority === 'HIGH' ? 'red' : '#01875F'}`
                                                    }}
                                                >
                                                    <Typography fontSize={13} color={item.priority === 'Medium' ? '#894B00' : item.priority === 'HIGH' ? 'red' : '#01875F'}>{item.priority}</Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                        {/* Expand/Collapse Icon */}
                                        <IconButton
                                            size="small"
                                            sx={{
                                                padding: '2px',
                                                color: theme.palette.icon.default,
                                            }}
                                        >
                                            {isExpanded ? <KeyboardArrowUp sx={{ color: theme.palette.background.dark }} /> : <KeyboardArrowDown sx={{ color: theme.palette.background.dark }} />}
                                        </IconButton>
                                    </Box>

                                    <Collapse
                                        in={isExpanded}
                                        timeout={300}
                                        easing={{
                                            enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
                                            exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
                                        }}
                                    >
                                        <Box mt={1}>
                                            <Typography fontSize={14} fontWeight={600}>Description</Typography>
                                            <Typography fontSize={13} color={theme.palette.background.dark}>{item.description}</Typography>
                                        </Box>
                                        <Box mt={1}>
                                            <Typography fontSize={14} fontWeight={600}>Linked College</Typography>
                                            <Typography fontSize={13} color={theme.palette.background.dark}>{item?.college?.college_name || '-'}</Typography>
                                        </Box>
                                        <Box
                                            display="flex"
                                            gap={15}
                                            mb={1}
                                        >
                                            <Box mt={1}>
                                                <Typography fontSize={14} fontWeight={600}>Reminder</Typography>
                                                <Typography fontSize={13} color={theme.palette.background.dark}>{Reminders.find((a) => a.value === item.reminder)?.label || 'None'}</Typography>
                                            </Box>
                                            <Box mt={1}>
                                                <Typography fontSize={14} fontWeight={600}>Repeat</Typography>
                                                <Typography fontSize={13} color={theme.palette.background.dark}>{Repeats.find((a) => a.value === item.repeat)?.label}</Typography>
                                            </Box>
                                        </Box>
                                        {user?.user_type_id === UserTypeId.Student && (
                                            <Box display="flex" gap={1} mt={2}>
                                                <CommonButton
                                                    label="Mark Complete"
                                                    size="small"
                                                    background={theme.palette.secondary.main}
                                                    color={theme.palette.background.main}
                                                    startIcon={<Check />}
                                                    fontWeight={700}
                                                    width='600px'
                                                    marginBottom="0px"
                                                    handleClick={async () => {
                                                        try {
                                                            dispatch(setLoading({
                                                                loading: true
                                                            }));
                                                            const response = await updateCalenderEvent(item?.event_id, {
                                                                user_id: user?.user_id,
                                                                status: EventStatus[3]
                                                            });
                                                            if (response.data?.event_id) {
                                                                showNotification('Calendar event mark completed successfully', 'success');
                                                                setViewOpen(false);
                                                                dispatch(setLoading({
                                                                    loading: false
                                                                }));
                                                            }
                                                        } catch (err) {
                                                            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                                            showNotification(errorMessage, 'error');
                                                            dispatch(setLoading({
                                                                loading: false
                                                            }));
                                                        }
                                                    }}
                                                />
                                                <CommonButton
                                                    label="Edit"
                                                    size="small"
                                                    background='white'
                                                    border={`1px solid ${theme.palette.background.light}`}
                                                    startIcon={<BorderColor sx={{ color: theme.palette.secondary.main, fontSize: 17 }} />}
                                                    color={theme.palette.secondary.main}
                                                    fontWeight={700}
                                                    marginBottom="0px"
                                                    handleClick={() => {
                                                        setSelectedEvent(item);
                                                        setEventOpen(true);
                                                    }}
                                                />
                                                <CommonButton
                                                    label="Remove"
                                                    background="white"
                                                    border={`1px solid ${theme.palette.background.light}`}
                                                    color="red"
                                                    startIcon={<DeleteForever sx={{ color: 'red', fontSize: 20 }} />}
                                                    fontWeight={700}
                                                    marginBottom="0px"
                                                    handleClick={() => {
                                                        setEventId(item.event_id);
                                                        setDeleteOpen(true);
                                                    }}
                                                />
                                            </Box>)}
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Box>
                }
            />
        </Fragment>
    )
}

export default ViewEventDialog;